import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Lock } from 'lucide-react';
import type { PlanRefusal } from '@tailoring-crm/shared';
import { formatCurrency } from '../../lib/format.js';
import { Button } from '@tailoring-crm/ui';

/**
 * The refusal, at the point of refusal.
 *
 * This renders where somebody was just stopped — inside the invite dialog, above the form
 * they cannot submit — rather than sending them to a billing screen to work out what went
 * wrong. The moment of refusal is the moment they are most willing to pay, and it is also the
 * only moment they have the context to judge whether it is worth it.
 *
 * Three rules the copy follows:
 *
 *  - NAME THE LIMIT, with the numbers. "Your plan includes 3 people and 3 are taken" is
 *    something an owner can act on; "upgrade required" is something they resent.
 *  - NAME THE PRICE. Sending someone to a pricing page to find out what the answer costs adds
 *    a step whose only effect is to lose some of them.
 *  - DO NOT SCOLD. They have not done anything wrong; they have outgrown a tier, which is the
 *    outcome everybody wanted.
 */

export interface UpgradePromptProps {
  refusal: PlanRefusal;
  /** Rendered inline in a dialog rather than as a page-level notice. */
  compact?: boolean;
}

export function UpgradePrompt({ refusal, compact = false }: UpgradePromptProps): ReactElement {
  const { t } = useTranslation();

  const headline =
    refusal.limit === 'staff_seats'
      ? t('billing.refusal.seats', {
          used: refusal.used ?? 0,
          allowance: refusal.allowance ?? 0,
        })
      : refusal.limit === 'module'
        ? t('billing.refusal.module', {
            module: t(`billing.module.${refusal.module ?? 'core'}`),
          })
        : t('billing.refusal.readOnly');

  // A payment problem has nothing to buy — offering "upgrade to Growth" to somebody whose card
  // failed would be answering a question they did not ask.
  const offer =
    refusal.requiredPlan && refusal.requiredPlanLabel
      ? t('billing.refusal.offer', {
          plan: refusal.requiredPlanLabel,
          // Compact: this is the advertised price, not a calculated amount.
          price: formatCurrency(refusal.requiredPlanMonthlyPricePaise ?? 0, { compact: true }),
        })
      : t('billing.refusal.noOffer');

  return (
    <div
      role="alert"
      className={`flex flex-col gap-3 rounded-lg border border-warning/40 bg-warning/10 ${
        compact ? 'p-3' : 'p-4'
      }`}
    >
      <div className="flex items-start gap-3">
        <Lock aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-warning" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{headline}</p>
          <p className="text-sm text-muted-foreground">{offer}</p>
          {/* Said out loud, every time. The fear behind "what happens if I downgrade?" is that
              the shop loses its books, and no amount of small print answers it as well as one
              sentence in the place they are worried. */}
          {refusal.limit === 'module' ? (
            <p className="mt-1 text-sm text-muted-foreground">{t('billing.refusal.dataSafe')}</p>
          ) : null}
        </div>
      </div>
      <div>
        <Button asChild size="sm" variant="outline">
          <Link to="/settings/billing">
            {t('billing.refusal.action')}
            <ArrowUpRight aria-hidden="true" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

export default UpgradePrompt;
